import React, { useState, useEffect } from 'react';
import { Box, Grid, CircularProgress } from '@mui/material';
import TeamInviteEmailsForm from 'team/components/adminInviteEmails/TeamInviteEmailsForm';
import TeamInviteEmailsPreview from 'team/components/adminInviteEmails/TeamInviteEmailsPreview';
import { useTeam } from 'team/contexts/teamContext';
import { updateTeam } from 'team/apis/teamApi';

const TeamAdminInviteEmailsContainer = () => {
    const { getCurrentTeam, currentTeamId, refreshAllData: refreshTeam } = useTeam();
    const teamData = getCurrentTeam();

    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isInitializing, setIsInitializing] = useState(true);

    // Form State (Lifted Up)
    const [subject, setSubject] = useState('[Action Required] You have been invited to Touch Teams');
    const [message, setMessage] = useState('');
    const [callToAction, setCallToAction] = useState('Join Now');
    const [showTouchInfo, setShowTouchInfo] = useState(true);

    const [initialSettings, setInitialSettings] = useState(null);

    // Default message template
    const defaultMessage = `Hi {firstName},

{teamName} is now using Touch digital business cards, and you have your very own digital business card waiting for you!

Click the link below to join your teammates and claim your new digital card from Touch.`;

    useEffect(() => {
        if (teamData) {
            let loadedSettings = {};
            if (teamData.settings && teamData.settings.inviteEmail) {
                const settings = teamData.settings.inviteEmail;
                loadedSettings = {
                    subject: settings.subject || '[Action Required] You have been invited to Touch Teams',
                    message: settings.message || defaultMessage,
                    callToAction: settings.callToAction || 'Join Now',
                    showTouchInfo: settings.showTouchInfo !== undefined ? settings.showTouchInfo : true
                };
            } else {
                loadedSettings = {
                    subject: '[Action Required] You have been invited to Touch Teams',
                    message: defaultMessage,
                    callToAction: 'Join Now',
                    showTouchInfo: true
                };
            }

            setSubject(loadedSettings.subject);
            setMessage(loadedSettings.message);
            setCallToAction(loadedSettings.callToAction);
            setShowTouchInfo(loadedSettings.showTouchInfo);

            setInitialSettings(loadedSettings);
            setIsInitializing(false);
        }
    }, [teamData]);

    // Check for changes
    const isDirty = initialSettings && (
        subject !== initialSettings.subject ||
        message !== initialSettings.message ||
        callToAction !== initialSettings.callToAction ||
        showTouchInfo !== initialSettings.showTouchInfo
    );

    const handleUpdate = async () => {
        setLoading(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {
            const updateData = {
                settings: {
                    inviteEmail: {
                        subject,
                        message,
                        callToAction,
                        showTouchInfo
                    }
                }
            };

            const response = await updateTeam(currentTeamId, updateData);

            if (response.success) {
                setSuccessMessage('Invite email settings updated successfully');
                await refreshTeam(); // Refresh team data in context
                // Update initial settings to current
                setInitialSettings({
                    subject,
                    message,
                    callToAction,
                    showTouchInfo
                });
            } else {
                setErrorMessage('Failed to update settings');
            }
        } catch (error) {
            console.error('Error updating invite email settings:', error);
            setErrorMessage(error.message || 'An error occurred while updating settings');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (initialSettings) {
            setSubject(initialSettings.subject);
            setMessage(initialSettings.message);
            setCallToAction(initialSettings.callToAction);
            setShowTouchInfo(initialSettings.showTouchInfo);
        }
    };

    if (isInitializing) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, height: '100%', overflow: 'hidden' }}>
            <Grid container spacing={4} sx={{ height: '100%' }}>
                {/* Left Panel - Form */}
                <Grid item xs={12} md={6} sx={{ height: '100%', overflowY: 'auto' }}>
                    <TeamInviteEmailsForm
                        subject={subject}
                        setSubject={setSubject}
                        message={message}
                        setMessage={setMessage}
                        callToAction={callToAction}
                        setCallToAction={setCallToAction}
                        showTouchInfo={showTouchInfo}
                        setShowTouchInfo={setShowTouchInfo}
                        loading={loading}
                        disabled={!isDirty}
                        successMessage={successMessage}
                        errorMessage={errorMessage}
                        onUpdate={handleUpdate}
                        onCancel={handleCancel}
                    />
                </Grid>

                {/* Right Panel - Preview */}
                <Grid item xs={12} md={6} sx={{ height: '100%', overflowY: 'auto' }}>
                    <TeamInviteEmailsPreview
                        subject={subject}
                        message={message}
                        callToAction={callToAction}
                        showTouchInfo={showTouchInfo}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default TeamAdminInviteEmailsContainer;
